import { createContext, useContext, useEffect, useMemo, useState } from 'react'
import { supabase } from '../lib/supabase.js'
import { canEditModelsClient } from '../lib/canEditModels.js'
import { FIREFLY_ROLES, normalizeAuthenticatedRole } from '../../lib/fireflyRoles.js'

const DEFAULT_ACCESS_PROFILE = {
  role: FIREFLY_ROLES.VISITOR,
  userType: FIREFLY_ROLES.VISITOR,
  isAdmin: false,
  permissions: []
}

const SupabaseAuthContext = createContext({
  user: null,
  session: null,
  loading: true,
  accessLoading: false,
  isSignedIn: false,
  userId: null,
  role: FIREFLY_ROLES.VISITOR,
  userType: FIREFLY_ROLES.VISITOR,
  isAdmin: false,
  permissions: [],
  accessProfile: DEFAULT_ACCESS_PROFILE,
  supabase,
  getToken: async () => null,
  signOut: async () => {}
})

export function SupabaseAuthProvider({ children }) {
  const [session, setSession] = useState(null)
  const [user, setUser] = useState(null)
  const [loading, setLoading] = useState(true)
  const [accessLoading, setAccessLoading] = useState(false)
  const [accessProfile, setAccessProfile] = useState(DEFAULT_ACCESS_PROFILE)

  useEffect(() => {
    let mounted = true

    supabase.auth.getSession()
      .then(({ data }) => {
        if (!mounted) return
        setSession(data?.session ?? null)
        setUser(data?.session?.user ?? null)
        setLoading(false)
      })
      .catch((error) => {
        console.warn('[SUPABASE_AUTH] Initial session refresh failed', error?.message || error)
        if (!mounted) return
        setSession(null)
        setUser(null)
        setAccessProfile(DEFAULT_ACCESS_PROFILE)
        setLoading(false)
      })

    const {
      data: { subscription }
    } = supabase.auth.onAuthStateChange((_event, newSession) => {
      setSession(newSession)
      setUser(newSession?.user ?? null)
    })

    return () => {
      mounted = false
      subscription?.unsubscribe()
    }
  }, [])

  useEffect(() => {
    let mounted = true

    const fallbackProfile = () => {
      const fallbackIsAdmin = canEditModelsClient(user)
      const role = fallbackIsAdmin ? FIREFLY_ROLES.ADMIN : FIREFLY_ROLES.CUSTOMER
      return {
        role,
        userType: role,
        isAdmin: fallbackIsAdmin,
        permissions: []
      }
    }

    const loadAccessProfile = async () => {
      if (loading) return

      if (!user?.id) {
        setAccessProfile(DEFAULT_ACCESS_PROFILE)
        setAccessLoading(false)
        return
      }

      if (!session?.access_token) {
        setAccessProfile(fallbackProfile())
        setAccessLoading(false)
        return
      }

      setAccessLoading(true)
      try {
        const response = await fetch('/api/admin/me', {
          headers: { Authorization: `Bearer ${session.access_token}` }
        })

        if (!response.ok) {
          throw new Error(`Access profile request failed: ${response.status}`)
        }

        const payload = await response.json()
        const data = payload?.data || {}
        const role = normalizeAuthenticatedRole(data.role)
        const isAdmin = data.isAdmin === true || role === FIREFLY_ROLES.ADMIN
        const permissions = Array.isArray(data.permissions) ? data.permissions : []

        if (mounted) {
          setAccessProfile({
            role,
            userType: data.userType || role,
            isAdmin,
            permissions
          })
        }
      } catch (error) {
        if (import.meta?.env?.DEV) {
          console.warn('[SUPABASE_AUTH] Falling back to local access profile', error)
        }
        if (mounted) {
          setAccessProfile(fallbackProfile())
        }
      } finally {
        if (mounted) {
          setAccessLoading(false)
        }
      }
    }

    loadAccessProfile()

    return () => {
      mounted = false
    }
  }, [loading, session?.access_token, user])

  const value = useMemo(() => {
    const getToken = async () => session?.access_token || null
    const signOut = async () => {
      await supabase.auth.signOut()
    }
    const enrichedUser = user ? {
      ...user,
      fireflyRole: accessProfile.role,
      userType: accessProfile.userType,
      isFireflyAdmin: accessProfile.isAdmin,
      fireflyPermissions: accessProfile.permissions
    } : null

    return {
      session,
      user: enrichedUser,
      loading,
      accessLoading,
      isSignedIn: !!user,
      userId: user?.id ?? null,
      role: accessProfile.role,
      userType: accessProfile.userType,
      isAdmin: accessProfile.isAdmin,
      permissions: accessProfile.permissions,
      accessProfile,
      supabase,
      getToken,
      signOut
    }
  }, [session, user, loading, accessLoading, accessProfile])

  return <SupabaseAuthContext.Provider value={value}>{children}</SupabaseAuthContext.Provider>
}

export function useSupabaseAuthContext() {
  const context = useContext(SupabaseAuthContext)
  if (!context) {
    throw new Error('useSupabaseAuthContext must be used within SupabaseAuthProvider')
  }
  return context
}

